import React from 'react';

import { Button, HStack, Popover, Text } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';

import { GeneralWrapper } from '@packages/components/generalWrapper/GeneralWrapper';
import { Table as UITable } from '@packages/components/table/Table';
import { getPrettyJson, truncateLabel } from '@packages/utils/base.utils';

import { useTicketEventsData } from './ticketQuery';

type TicketEventsProps = { ticketId: string; attachmentId: string };
type CellData = { cell: { value: string } };

const ROWS_PER_PAGE = 5;

export const TicketEvents: React.FC<TicketEventsProps> = ({
  ticketId,
  attachmentId,
}) => {
  const { t } = useTranslation();
  const { isPending, isError, data } = useTicketEventsData(
    {
      attachmentId,
      ticketId,
    },
    t,
  );

  const columns = data?.columns.map((column) => ({
    ...column,
    Cell: ({ cell: { value } }: CellData) => {
      const maxLength = 50;
      if (value && value.length > maxLength) {
        return (
          <HStack gap="xsmall">
            <Text styledAs="body3" lineHeight={0} whiteSpace="nowrap">
              {truncateLabel(value, maxLength)}
            </Text>
            <Popover.Root>
              <Popover.Trigger asChild>
                <Button variant="link">{t('ui:more')}</Button>
              </Popover.Trigger>
              <Popover.Content
                side="bottom"
                align="end"
                width="45rem"
                padding="medium"
                backgroundColor="widgetTertiary"
                maxHeight="20rem"
                overflow="auto"
              >
                <Text styledAs="body3" data-testid="more">
                  {getPrettyJson(value)}
                </Text>
              </Popover.Content>
            </Popover.Root>
          </HStack>
        );
      }
      return <Text styledAs="body3">{value}</Text>;
    },
  }));

  return (
    <GeneralWrapper
      isLoading={isPending}
      isError={isError}
      isNoData={data?.total === 0}
    >
      <UITable
        data={data?.rows ?? []}
        columns={columns ?? []}
        rowsPerPage={ROWS_PER_PAGE}
        totalRows={data?.total ?? 0}
        showPagination
        manualSortBy={false}
        manualPagination={false}
        enableGlobalFilter
      />
    </GeneralWrapper>
  );
};
